import React, { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import ProductFormWrapper from '../components/ProductFormWrapper'
import RadioButtonField from '../../../../../common/components/radio-button-field/RadioButtonField'
import { SecondStepOfSolarProductDetailsState, submitSecondStep } from '../../../../../stores/slices/solar-product-details-slice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../stores/store'
import { SolarProductType } from '../../../../../model/solar-product/solar-product-type'
import { trimFormValues } from '../../../../../common/utils/trimFormValues'

type Props = {}


const SecondStepOfProductDetails: React.FC<Props> = memo((props: Props) => {
    const dispatch = useDispatch();

    const secondStepData = useSelector((state: RootState) => state.solarProductDetails.secondStep)

    const {
        register,
        watch,
        handleSubmit,
        setValue,
        formState: { errors, isValid, isSubmitted },
    } = useForm<SecondStepOfSolarProductDetailsState>({
        defaultValues: secondStepData,
        reValidateMode: 'onChange',
        mode: 'onTouched'
    })

    const submit = (data: any) => {
        dispatch(submitSecondStep(trimFormValues(data)));
    }

    const registerType = register('type', { required: 'Type is required' });

    useEffect(() => {
        const fieldsToSet = ['inverterType', 'warranty', 'batteryVoltage', 'type', 'quantityAvailableForSale'];

        fieldsToSet.forEach((field) => {
            if (!secondStepData[field as keyof SecondStepOfSolarProductDetailsState]) {
                setValue(field as keyof SecondStepOfSolarProductDetailsState, secondStepData[field as keyof SecondStepOfSolarProductDetailsState]);
                return;
            }

            setValue(field as keyof SecondStepOfSolarProductDetailsState, secondStepData[field as keyof SecondStepOfSolarProductDetailsState], {
                shouldValidate: true,
            });
        });
    }, [secondStepData, setValue])

    return (
        <ProductFormWrapper
        isValid={isValid}
        isSubmitted={isSubmitted}
        title='Product Details'
        onNext={handleSubmit(submit)}
        >
            <CommonFormFieldWrapper
                title="Inverter Type/Name"
                errors={getFormInputErrors(errors.inverterType?.message)}>
                <input
                    {...register(
                        'inverterType',
                    )}
                    placeholder="Enter Inverter Type/Name" />
            </CommonFormFieldWrapper>
            <span className='spacer' />
            <CommonFormFieldWrapper
                title="Warranty"
                errors={getFormInputErrors(errors.warranty?.message)}>
                <input
                    {...register(
                        'warranty',
                    )}
                    placeholder="Enter Warranty" />
            </CommonFormFieldWrapper>
            <span className='spacer' />
            <CommonFormFieldWrapper
                title="Battery Voltage"
                errors={getFormInputErrors(errors.batteryVoltage?.message)}>
                <input
                    type='number'
                    {...register('batteryVoltage')}
                    placeholder="Enter Battery Voltage" />
            </CommonFormFieldWrapper>
            <span className='spacer' />
            <CommonFormFieldWrapper
                title="Chose One"
                required={true}
                errors={getFormInputErrors(errors.type?.message)}
                borderNotVisible={true}>
                <RadioButtonField text="Storage Only"
                    registerProp={registerType}
                    initialValue={watch('type')}
                    selectedValue={SolarProductType.STORAGE_ONLY}
                    onChangeValue={(value: SolarProductType) => setValue('type', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })} 
                    />
                <RadioButtonField text="Storage and Panel"
                    registerProp={registerType}
                    initialValue={watch('type')}
                    selectedValue={SolarProductType.STORAGE_AND_PANEL}
                    onChangeValue={(value: SolarProductType) => setValue('type', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })} 
                    />
            </CommonFormFieldWrapper>
            <span className='spacer' />
            <div className="formFieldInlineContainer">
                <CommonFormFieldWrapper 
                title="Quantity available for Sale"
                errors={getFormInputErrors(errors.quantityAvailableForSale?.message)}
                >
                    <input
                        type='number'
                        {...register('quantityAvailableForSale', {
                            min: {
                                value: 0,
                                message: 'Quantity available for Sale cannot be less than 0'
                            }
                        })}
                        placeholder="Enter Quantity available for Sale" />
                </CommonFormFieldWrapper>
            </div>
        </ProductFormWrapper>
    )
})

export default SecondStepOfProductDetails